<script setup lang="ts">
import {useRoute, useRouter} from "vue-router";
import { onMounted } from "@vue/runtime-core";
import { useModyApi } from "~/middelware/ModyApi";
import { Product } from "~/utils/Product";
import {useAsyncData} from "#app";
import type { MenuItem } from "primevue/menuitem";
import {Category} from "~/utils/Category";
import {useUserDataStorage} from "~/store/userdata";
import {useMyFunctions} from "~/middelware/MyFunctions";
import {useToast} from "primevue/usetoast";
import Galleria from "primevue/galleria";
import type { CartProduct } from "#imports";
import { addToCart } from "~/utils/Cart";

const route = useRoute();
const userStore = useUserDataStorage()
const router = useRouter();
const _product = ref<Product>();
const api = useModyApi();
const myFunctions = useMyFunctions()
const amount = ref(1);
const imgLoaded = ref(false)
const accordionOpen = ref<Number|null>(null)
const toast = useToast()
const homeMenuItem = ref<MenuItem>({
  icon:"pi pi-home",
  route: "/shop"
})
const breadCrumbMenuItems = ref<MenuItem[]>([])

const { data } = await useAsyncData('products', ()=> loadData())

onMounted(()=>{
  _product.value = data.value as Product
  if(_product.value?.categories.length > 0){
    addBreadcrumbForCategory(_product.value?.categories[0])
  }
  if(myFunctions.isDesktop.value){
    accordionOpen.value = 0
  }
})

watch(route,async ()=>{
  _product.value = await loadData()
})

function addBreadcrumbForCategory(category:Category){
  breadCrumbMenuItems.value.unshift({
    label:category.name,
    route:"/shop/category/"+category.id
  })
  if(category.parentCategory){
    addBreadcrumbForCategory(category.parentCategory)
  }
}

useSeoMeta({
  title: "Mody Veranstaltungstechnik | " + data.value?.Name,
  description: data.value?.Description,
  ogTitle: "Mody Veranstaltungstechnik | " + data.value?.Name,
  ogDescription: data.value?.Description,
  ogImage: data.value?.Images[0],
  twitterCard: 'summary_large_image',
})
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": data.value?.Name,
        "image": data.value?.Images[0],
        "description": data.value?.Description,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "ratingCount": "6"
        }
      }
    }
  ]
})

async function loadData(){
  if(route.params.id){//){
    const productId:number = Number(route.params.id)
    const product = await api.getProduct(productId);
    return product.toJSON()
  }
}
</script>
<template>
  <Toast>
    <template #message="slotProps">
      <div style="width:100%">
        {{slotProps.message.detail}}
        <div @click.stop="router.push('/shop/request')" style="text-decoration: underline;cursor:pointer">
          zur Anfrage
        </div>
      </div>
    </template>
  </Toast>
  <div class="split-container">
    <div class="product-container">
      <Breadcrumb :home="homeMenuItem" :model="breadCrumbMenuItems" >
        <template #item="{ item, props }">
          <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" @click="navigate">
              <span :class="[item.icon, 'text-color']" />
              <span class="font-semibold">{{ item.label }}</span>
            </a>
          </router-link>
          <a v-else :href="item.url" :target="item.target" v-bind="props.action">
            <span class="text-color">{{ item.label }}</span>
          </a>
        </template>
      </Breadcrumb>
      <div class="product" v-if="_product">
        <Galleria :value="_product.Images" :hidden="!imgLoaded"
                  :showThumbnails="false" :showIndicators="_product.Images.length > 1">
          <template #item="slotProps">
            <Image class="image border-none" :class="{imageCover:!_product.transparentImage}" v-if="_product.Images" :src="slotProps.item" preview   @load="imgLoaded=true"></Image>
          </template>
        </Galleria>
        <Skeleton class="image" v-if="!imgLoaded" height="15rem"></Skeleton>
        <div class="info">
          <h1 class="name">{{ data?.Name }}</h1>

          <div class="price">
            <template v-if="_product.Price != 0">{{ _product.Price.toLocaleString("de-AT", {style: "currency", currency: "EUR"}) }}</template>
            <template v-else>Preis auf Anfrage</template>
          </div>
          <div class="add-to-cart">
            <InputNumber @click.stop v-model="amount" inputId="integeronly" showButtons buttonLayout="horizontal"/>
            <Button @click.stop="addToCart(amount,_product,toast)">Anfragen</Button>
          </div>
          <Accordion class="details" v-model:activeIndex="accordionOpen">
            <AccordionTab header="Details" v-if="_product.Description">
              <p v-html="_product.Description"></p>
            </AccordionTab>
            <AccordionTab header="Downloads" v-if="_product.manualUrl">
              <Button>
                <a :href="_product.manualUrl" target="_blank">Bedienungsanleitung</a>
              </Button>
            </AccordionTab>
          </Accordion>
        </div>
      </div>
      <div v-else>
        <Skeleton height="15rem" style="margin-bottom:20px"></Skeleton>
        <Skeleton height="2rem" width="15rem" style="margin-bottom:20px"></Skeleton>
        <Skeleton height="1rem" width="10rem" style="margin-bottom:20px"></Skeleton>
      </div>
      <div class="rentNowContainer">
        <h3>Jetzt mieten!</h3>
        <p>Du willst dieses Produkt ausleihen? Dann stelle einfach eine Anfrage und wir melden uns bei dir.</p>
        <Button class="mt-3" @click="router.push('/shop/request')">Anfrage stellen</Button>
      </div>
    </div>
    <ShopSideMenu class="shopSideMenu"></ShopSideMenu>
  </div>
</template>
<style scoped lang="scss">

.p-breadcrumb{
  background:none;
}
.product {
  width: 100%;

  :deep(.p-galleria){
    border: none;
      border-radius:20px;
  }
  :deep(.p-image){
    width: 100%;
    &.imageCover {
      img{
        background:none;
        height: auto;
      }
    }
    img{
      width:100%;
      height: 400px;
      object-fit: contain;
      background-color: $imgBackground;
      border-radius:20px;
    }

  }
  .info{
    margin-top:30px;
    padding: 0 20px 20px 20px;
    .name{
      font-size: 16px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .price{
      margin-top:10px;
      font-size: 24px;
      font-weight: 100;
    }
    .add-to-cart{
      margin-top:20px;
      display: flex;
      justify-content:space-between;
      :deep(.p-inputtext){
        width:70px;
      }
    }
    .details{
      margin-top:50px;
      :deep(.p-accordion-header-action){
        background-color: unset;
      }
      :deep(.p-accordion-content){
        background-color: unset;
      }
      :deep(p){
        margin-bottom: 5px;
        width: 100%;
        &:empty {
          display: block;
          min-height: 1em;
        }
      }
      :deep(ul){
        list-style: circle;
        padding-left: 50px;
      }
    }
  }

  @include desktop(){
    display: flex;
    width:$desktop-content-width;
    margin:auto;
    :deep(.p-galleria){
      width:50%;
    }
    :deep(.p-image){
      width:100%;
      img{
        width:100%
      }
    }
    .info{
      margin-top:0;
      width:50%;
      .details{
        margin-top:30px;
      }
    }
  }
}

.split-container{
  display:flex;
  .product-container{
    width:100%;
  }
}
.shopSideMenu{
  display:none;
  margin: 0;
  width:fit-content;
  @include desktop(){
    display:unset;
    width: 350px;
  }
}
.rentNowContainer{
  display:block;
  margin:auto;
  padding:20px;
  @include desktop(){
    width:800px;
  }
}
</style>
<style lang="scss">

.p-image-mask{
  --maskbg: rgba(145, 145, 145, 0.9);
}
</style>
